import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Badge, Spinner, Form, Modal } from 'react-bootstrap';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { storeTransaction } from '../../api/classes';
import {
  getCourseDetailById, getCourseReviews, addCourseReview, updateCourse, deleteCourse,
  getCourseResources, addCourseResources, deleteCourseResources,
  getCourseLessons, addCourseLesson, getCourseLessonById, deleteCourseLesson, updateCourseLesson,
  markLessonCompleted, submitCourseLessonAnswer, registerUserToCourse, createCourseCertificate
} from '../../api/courses';
import DummyUser from '../../assets/images/avatar.png';
import brandLogo from '../../assets/images/logo.png';
import './CourseDetail.scss';

// ── Safe string extractors ──────────────────────────────────────────────────
const safeStr = (val, fallback = '') => {
  if (val === undefined || val === null) return fallback;
  if (typeof val === 'object') {
    return val.name || val.full_name || val.title || fallback;
  }
  return String(val);
};

// Convert a YouTube URL (watch or youtu.be) to an embeddable URL
const toYouTubeEmbed = (url) => {
  if (!url) return null;
  // youtu.be/ID
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
  // youtube.com/watch?v=ID
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
  return url;
};

const renderVideoPlayer = (url) => {
  if (!url) return null;
  const isYouTube = url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com');
  if (isYouTube) {
    const embedUrl = toYouTubeEmbed(url);
    return (
      <iframe src={embedUrl} title="Video" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" className="w-100 h-100 border-0" />
    );
  }
  return <video controls src={url} className="w-100 h-100 object-fit-cover bg-dark" />;
};

// Render star icons based on a numeric rating (0-5)
const StarRating = ({ rating = 0, max = 5 }) => {
  const stars = [];
  for (let i = 1; i <= max; i++) {
    if (i <= Math.floor(rating)) {
      stars.push(<i key={i} className="bi bi-star-fill text-warning" />);
    } else if (i - rating < 1) {
      stars.push(<i key={i} className="bi bi-star-half text-warning" />);
    } else {
      stars.push(<i key={i} className="bi bi-star text-warning" />);
    }
  }
  return <span className="stars-row">{stars}</span>;
};

const QuestionEditor = ({ questions, setQuestions }) => {
  const addQuestion = () => setQuestions([...questions, { question: '', quizType: 'singleChoice', options: [{ text: '', isCorrect: false }], paragraphRightAnswer: '' }]);
  const removeQuestion = (idx) => setQuestions(questions.filter((_, i) => i !== idx));
  const updateQuestion = (idx, field, value) => {
    const q = [...questions];
    q[idx][field] = value;
    if (field === 'quizType') {
      if (value === 'paragraph') q[idx].options = [];
      else if (!q[idx].options || q[idx].options.length === 0) q[idx].options = [{ text: '', isCorrect: false }];
    }
    setQuestions(q);
  };
  const addOption = (idx) => {
    const q = [...questions];
    q[idx].options = q[idx].options || [];
    q[idx].options.push({ text: '', isCorrect: false });
    setQuestions(q);
  };
  const removeOption = (qIdx, oIdx) => {
    const q = [...questions];
    if (q[qIdx].options) q[qIdx].options = q[qIdx].options.filter((_, i) => i !== oIdx);
    setQuestions(q);
  };
  const updateOption = (qIdx, oIdx, field, value) => {
    const q = [...questions];
    if (q[qIdx].options) q[qIdx].options[oIdx][field] = value;
    setQuestions(q);
  };

  return (
    <div className="mt-4 border-top pt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-semibold mb-0">Quiz Questions (Optional)</h6>
        <Button variant="outline-primary" size="sm" onClick={addQuestion}>+ Add Question</Button>
      </div>
      {questions.map((q, qIdx) => (
        <div className="border rounded p-3 mb-3 bg-light" key={qIdx}>
          <div className="d-flex justify-content-between mb-2">
            <span className="fw-bold fs-sm">Question {qIdx + 1}</span>
            <Button variant="outline-danger" size="sm" className="p-1 px-2" onClick={() => removeQuestion(qIdx)}><i className="bi bi-trash" /></Button>
          </div>
          <Form.Group className="mb-2">
            <Form.Control type="text" placeholder="Enter question..." value={q.question} onChange={(e) => updateQuestion(qIdx, 'question', e.target.value)} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Select size="sm" value={q.quizType || 'singleChoice'} onChange={(e) => updateQuestion(qIdx, 'quizType', e.target.value)}>
              <option value="singleChoice">Single Choice</option>
              <option value="multipleChoice">Multiple Choice</option>
              <option value="paragraph">Paragraph (Text Answer)</option>
            </Form.Select>
          </Form.Group>

          {q.quizType === 'paragraph' ? (
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm fw-semibold text-muted">Correct Answer (Optional)</Form.Label>
              <Form.Control as="textarea" rows={2} placeholder="Enter paragraph correct answer..." value={q.paragraphRightAnswer || ''} onChange={(e) => updateQuestion(qIdx, 'paragraphRightAnswer', e.target.value)} />
            </Form.Group>
          ) : (
            <div className="mt-3 ps-3 border-start border-2 border-primary">
              <span className="fw-semibold fs-sm mb-2 d-block text-muted">Options</span>
              {q.options?.map((opt, oIdx) => (
                <div key={oIdx} className="d-flex align-items-center mb-2 gap-2">
                  <Form.Check
                    type={q.quizType === 'multipleChoice' ? 'checkbox' : 'radio'}
                    name={`q-${qIdx}-correct`}
                    checked={opt.isCorrect}
                    onChange={(e) => {
                      const updated = [...questions];
                      if (q.quizType === 'singleChoice') {
                        updated[qIdx].options.forEach(o => o.isCorrect = false);
                      }
                      updated[qIdx].options[oIdx].isCorrect = e.target.checked;
                      setQuestions(updated);
                    }}
                    title="Mark as correct"
                  />
                  <Form.Control type="text" size="sm" placeholder={`Option ${oIdx + 1}`} value={opt.text} onChange={(e) => updateOption(qIdx, oIdx, 'text', e.target.value)} required />
                  {q.options.length > 1 && (
                    <Button variant="outline-secondary" size="sm" className="p-1 px-2 border-0" onClick={() => removeOption(qIdx, oIdx)}><i className="bi bi-x" /></Button>
                  )}
                </div>
              ))}
              <Button variant="link" size="sm" className="p-0 text-decoration-none mt-1" onClick={() => addOption(qIdx)}>+ Add Option</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [openModuleId, setOpenModuleId] = useState('m1');
  const [showPaypal, setShowPaypal] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);

  // API reviews & interaction states
  const [courseReviews, setCourseReviews] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newRating, setNewRating] = useState(5);
  const [newReviewText, setNewReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  // Lessons & interactive learning states
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonDetail, setLessonDetail] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [submittedQuizzes, setSubmittedQuizzes] = useState({});
  const [showAddLessonModal, setShowAddLessonModal] = useState(false);
  const [submittingLesson, setSubmittingLesson] = useState(false);

  // New Lesson form states
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [newLessonDesc, setNewLessonDesc] = useState('');
  const [newLessonVideo, setNewLessonVideo] = useState('');
  const [newLessonVideoFile, setNewLessonVideoFile] = useState(null);
  const [newLessonQuestions, setNewLessonQuestions] = useState([]);

  // Edit Lesson states
  const [showEditLessonModal, setShowEditLessonModal] = useState(false);
  const [updatingLesson, setUpdatingLesson] = useState(false);
  const [editLessonId, setEditLessonId] = useState(null);
  const [editLessonTitle, setEditLessonTitle] = useState('');
  const [editLessonDesc, setEditLessonDesc] = useState('');
  const [editLessonVideo, setEditLessonVideo] = useState('');
  const [editLessonVideoFile, setEditLessonVideoFile] = useState(null);
  const [editLessonQuestions, setEditLessonQuestions] = useState([]);

  const handleOpenEditLessonModal = (lesson) => {
    setEditLessonId(lesson._id || lesson._id);
    setEditLessonTitle(lesson.title || '');
    setEditLessonDesc(lesson.description || '');
    setEditLessonVideo(lesson.video_url || lesson.media || lesson.videoUrl || '');
    setEditLessonVideoFile(null);
    setEditLessonQuestions(lesson.questions || []);
    setShowEditLessonModal(true);
  };

  // Course Materials states
  const [resources, setResources] = useState([]);
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const [newResourceName, setNewResourceName] = useState('');
  const [newResourceFile, setNewResourceFile] = useState(null);
  const [submittingResource, setSubmittingResource] = useState(false);

  // Certificate Modal state
  const [certificateData, setCertificateData] = useState(null);
  const [showCertModal, setShowCertModal] = useState(false);

  // Form states for update course
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editPrice, setEditPrice] = useState(0);
  const [editCover, setEditCover] = useState('');
  const [editCoverFile, setEditCoverFile] = useState(null);

  // Static curriculum modules – API does not return lesson structure yet
  const modules = [];

  useEffect(() => {
    const fetchCourseDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getCourseDetailById(id);
        // API returns { status, message, data }
        if (res && res.data) {
          setCourseData(res.data);
          setEditTitle(res.data.title || '');
          setEditDesc(res.data.description || '');
          setEditPrice(res.data.price || 0);
          setEditCover(res.data.cover_image || res.data.coverImage || '');
          setCourseReviews(Array.isArray(res.data.course_reviews) ? res.data.course_reviews : []);
        } else {
          throw new Error('Course details not found in response');
        }

        try {
          const reviewsRes = await getCourseReviews(id);
          if (reviewsRes && reviewsRes.data) {
            setCourseReviews(reviewsRes.data);
          } else if (Array.isArray(reviewsRes)) {
            setCourseReviews(reviewsRes);
          }
        } catch (revErr) {
          console.warn('Failed to load reviews from API', revErr);
        }

        try {
          const resRes = await getCourseResources(id);
          if (resRes && resRes.data) {
            setResources(resRes.data);
          } else if (Array.isArray(resRes)) {
            setResources(resRes);
          }
        } catch (resErr) {
          console.warn('Failed to load resources from API', resErr);
        }

        try {
          const lessonsRes = await getCourseLessons(id);
          if (lessonsRes && lessonsRes.data) {
            setLessons(lessonsRes.data);
          } else if (Array.isArray(lessonsRes)) {
            setLessons(lessonsRes);
          }
        } catch (lesErr) {
          console.warn('Failed to load lessons from API', lesErr);
        }

      } catch (err) {
        console.error('Failed to fetch course details:', err);
        setError('Failed to load course details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchCourseDetails();
  }, [id]);

  const toggleModule = (modId) => {
    setOpenModuleId((prev) => (prev === modId ? null : modId));
  };

  const handleSelectLesson = async (lesson) => {
    try {
      setLessonLoading(true);
      setSelectedLesson(lesson);
      setIsPlaying(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (lesson._id) {
        const res = await getCourseLessonById(lesson._id);
        if (res && res.data) {
          setLessonDetail(res.data);
        } else {
          setLessonDetail(lesson);
        }
      } else {
        setLessonDetail(lesson);
      }
    } catch (err) {
      setLessonDetail(lesson);
    } finally {
      setLessonLoading(false);
    }
  };

  const handleMarkCompleted = async (lessonId) => {
    if (!lessonId) return alert("This is a preview lesson and cannot be marked as completed.");
    try {
      await markLessonCompleted({ course_id: id, courseId: id, lesson_id: lessonId, lessonId });
      alert('Lesson completed!');
      const refreshL = await getCourseLessons(id);
      setLessons(refreshL?.data || refreshL || []);
    } catch (err) {
      alert('Failed to mark lesson completed.');
    }
  };

  const handleSubmitQuizAnswer = async (e, lessonId) => {
    e.preventDefault();
    if (!lessonId) return alert("This is a preview lesson and quizzes cannot be submitted.");
    try {
      const answersPayload = Object.values(quizAnswers).map(ans => ({ ...ans, lessonId }));
      if (answersPayload.length === 0 && !answerText.trim()) return;

      const payload = answersPayload.length > 0 ? { lesson_id: lessonId, lessonId, answers: answersPayload } : { lesson_id: lessonId, lessonId, answer: answerText };
      await submitCourseLessonAnswer(payload);

      alert('Quiz submitted successfully!');
      setSubmittedQuizzes(prev => ({ ...prev, [lessonId]: true }));
      handleMarkCompleted(lessonId);
    } catch (err) {
      alert('Failed to submit quiz.');
    }
  };

  const handleClaimCertificate = async () => {
    try {
      const res = await createCourseCertificate({ course_id: id, courseId: id });
      setCertificateData({
        ...(res?.data || {}),
        studentName: 'Student',
        courseTitle: courseData?.title,
        date: new Date().toLocaleDateString()
      });
      setShowCertModal(true);
    } catch (err) {
      setCertificateData({
        studentName: 'Student',
        courseTitle: courseData?.title,
        date: new Date().toLocaleDateString()
      });
      setShowCertModal(true);
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center bg-light" style={{ minHeight: '80vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-gray-700 fw-semibold">Loading course details...</p>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error || !courseData) {
    return (
      <Container className="py-5 text-center">
        <div className="mb-4">
          <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '3rem' }} />
        </div>
        <h3 className="text-danger mb-2">Failed to load course</h3>
        <p className="text-gray-700 mb-4">{error || 'Course not found.'}</p>
        <Link to="/courses" className="btn btn-primary rounded-pill px-4">
          Back to Courses
        </Link>
      </Container>
    );
  }

  // ── Derived values from API response ──────────────────────────────────────
  const instructor = courseData.instructor || courseData.createdBy || {};
  const category = courseData.category || {};

  const categoryName = courseData.category_name || safeStr(category, 'Education');
  const instructorName = safeStr(instructor.full_name || instructor.name, 'YOOSPEAK APP');
  const instructorAvatar = instructor.profile_picture || instructor.avatar || instructor.image || DummyUser;
  const instructorCountry = safeStr(instructor.country, 'United States');
  const instructorLanguages = instructor.languages_string || (instructor.languages ? instructor.languages.map((l) => l.name).join(', ') : 'English');
  const followersCount = typeof instructor.followers === 'number' ? instructor.followers : 0;
  const followingCount = typeof instructor.following === 'number' ? instructor.following : 0;
  const experienceYears = typeof instructor.experience === 'number' ? instructor.experience : 0;
  const instructorRating = instructor.reviews?.average_rating || 0;
  const instructorReviewCount = instructor.reviews?.total_reviews || 0;
  const isVerified = instructor.is_blue_verified ?? false;

  const priceVal = typeof courseData.price === 'number' ? courseData.price : Number(courseData.price) || 0;
  const priceText = priceVal > 0 ? `$${priceVal}` : 'Free';
  const courseType = courseData.type || (priceVal > 0 ? 'paid' : 'free');

  const rating = courseData.rating || {};
  const ratingVal = Number(rating.average_rating || courseData.average_rating || 0).toFixed(1);
  const reviewsCount = rating.total_reviews || courseData.reviews_count || 0;
  const enrolledCount = courseData.students || 0;
  const courseDuration = courseData.duration || '—';

  const courseImage = courseData.coverImage || courseData.cover_image || courseData.image || 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?w=800&h=450&fit=crop';
  const coverVideoUrl = courseData.coverVideo || courseData.cover_video;
  const activeVideoUrl = selectedLesson ? (selectedLesson.video_url || lessonDetail?.video_url || selectedLesson.media || lessonDetail?.media) : coverVideoUrl;

  const progress = courseData.progress || {};
  const completedLessons = progress.no_of_completed_lesson || 0;
  const totalLessons = progress.total_no_of_lesson || 0;
  const progressPct = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  const isEnrolled = courseData.is_enrolled || false;
  const isInstructor = courseData.is_instructor || false;

  const completedCount = lessons.filter(l => l.isVideoCompleted || l.is_completed || l.completed).length;
  const isCourseCompleted = lessons.length > 0 && completedCount === lessons.length;

  // Build curriculum modules dynamically
  const dynamicModules = [];
  if (lessons.length > 0) {
    lessons.forEach((lesson, index) => {
      const modName = lesson.module_name || lesson.module || `Module ${Math.floor(index / 5) + 1}`;
      let mod = dynamicModules.find((m) => m.title === modName);
      if (!mod) {
        mod = {
          id: `mod-${dynamicModules.length + 1}`,
          title: modName,
          lessons: [],
        };
        dynamicModules.push(mod);
      }
      mod.lessons.push({
        ...lesson,
        id: lesson._id || lesson.id,
        title: lesson.title,
        duration: lesson.duration || lesson.videoDuration || '05:00',
        isLocked: isInstructor ? false : (lesson.is_enable_next === false ? true : (!isEnrolled && index > 0)),
        video_url: lesson.video_url || lesson.videoUrl || lesson.media || '',
        description: lesson.description || '',
        is_completed: lesson.isVideoCompleted || lesson.is_completed || lesson.completed,
      });
    });
  } else {
    // Fallback to static modules if no dynamic lessons fetched yet
    dynamicModules.push(...modules);
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="course-detail-page bg-light pt-4">

      {/* Breadcrumbs */}
      <Container fluid="xl">
        <div className="custom-breadcrumbs d-flex flex-wrap row-gap-1 column-gap-2 fw-medium text-sm text-gray-700 mb-3">
          <Link to="/courses" className="text-decoration-none text-gray-700">Courses</Link>
          <span><i className="bi bi-chevron-right"></i></span>
          <span>{categoryName}</span>
          <span><i className="bi bi-chevron-right"></i></span>
          <span className="text-gray-900">{courseData.title}</span>
        </div>

        <Row className="g-4">

          {/* ── LEFT SIDEBAR: Curriculum ─────────────────────────────────── */}
          <Col xl={3} lg={4} className="curriculum-sidebar-col">
            <div className="curriculum-sidebar bg-white border border-light-subtle rounded-3 shadow-sm px-2 py-3">

              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="sidebar-title fs-6 fw-bold text-navy m-0">Course Curriculum</div>
                {isInstructor && (
                  <Button variant="link" className="p-0 text-primary text-decoration-none fw-bold text-xs" onClick={() => setShowAddLessonModal(true)}>
                    + Add Lesson
                  </Button>
                )}
              </div>

              {/* Progress bar */}
              <div className="curriculum-progress-section mb-3">
                <div className="d-flex justify-content-between text-xs text-gray-700 mb-1">
                  <span>{progressPct}% Complete</span>
                  {totalLessons > 0 && (
                    <span>{completedLessons}/{totalLessons} Lessons</span>
                  )}
                </div>
                <div className="progress-bar-bg bg-purple bg-opacity-25 rounded-pill" style={{ height: '6px', background: '#f1f5f9' }}>
                  <div
                    className="progress-bar-fill bg-primary rounded-pill"
                    style={{ width: `${progressPct}%`, height: '100%', transition: 'width 0.4s ease' }}
                  />
                </div>
              </div>

              {/* Course Intro Row */}
              <div
                className={`course-intro-pill d-flex align-items-center gap-2 px-3 py-2 rounded mb-3 pointer ${!selectedLesson ? 'active' : ''}`}
                onClick={() => { setSelectedLesson(null); setLessonDetail(null); setIsPlaying(false); }}
              >
                <i className="bi bi-play-circle-fill text-purple" />
                <span className="text-xs fw-semibold">Course Introduction</span>
                <span className="ms-auto text-xs text-gray-700">0/1</span>
              </div>

              {/* Modules Accordion */}
              <div className="modules-accordion d-flex flex-column gap-2">
                {dynamicModules.map((mod) => {
                  const isOpen = openModuleId === mod.id;
                  return (
                    <div key={mod.id} className="module-item border border-light-subtle rounded-3 overflow-hidden">
                      <button
                        onClick={() => toggleModule(mod.id)}
                        className="module-header-btn d-flex align-items-start justify-content-between w-100 p-3 border-0 bg-transparent text-start"
                      >
                        <div>
                          <div className="module-title text-sm fw-bold text-navy mb-1">{mod.title}</div>
                          <div className="module-meta text-xs text-gray-700">{mod.lessons.length} Lessons</div>
                        </div>
                        <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} text-gray-700`} />
                      </button>

                      {isOpen && (
                        <div className="module-lessons-list d-flex flex-column gap-1 p-2 pt-0">
                          {mod.lessons.map((lesson) => {
                            const isCompleted = lesson.is_completed || lesson.completed;
                            const isCurrent = selectedLesson && (selectedLesson._id === lesson._id || selectedLesson.id === lesson.id);
                            return (
                              <div
                                key={lesson.id}
                                className={`lesson-row-item d-flex align-items-center justify-content-between fw-medium gap-2 p-2 rounded-2 text-xs ${isCurrent ? 'bg-primary text-white pointer' : !lesson.isLocked ? 'active bg-purple bg-opacity-10 pointer' : 'text-gray-700'}`}
                                onClick={() => {
                                  if (!lesson.isLocked || isInstructor) {
                                    handleSelectLesson(lesson);
                                  } else {
                                    alert('Enroll to view this lesson');
                                  }
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 min-w-0">
                                  <i className={`bi bi-${lesson.isLocked ? 'lock-fill' : 'play-circle-fill'} ${isCurrent ? 'text-white' : !lesson.isLocked ? 'text-purple' : 'text-gray-700'}`} />
                                  <span className={`lesson-name ${isCurrent ? 'text-white' : !lesson.isLocked ? 'text-purple' : 'text-gray-700'} text-truncate`}>{lesson.title}</span>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                  <span className={isCurrent ? 'text-white-50' : 'text-gray-700'}>{lesson.duration}</span>
                                  {isCompleted && <i className="bi bi-check-circle-fill text-success" />}
                                  {isInstructor && (
                                    <>
                                      <Button variant="link" className="text-primary p-0 ms-1" onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenEditLessonModal(lesson);
                                      }}>
                                        <i className="bi bi-pencil" />
                                      </Button>
                                      <Button variant="link" className="text-danger p-0 ms-2" onClick={async (e) => {
                                        e.stopPropagation();
                                        if (window.confirm('Delete this lesson?')) {
                                          try {
                                            await deleteCourseLesson(lesson._id);
                                            alert('Lesson deleted.');
                                            const refreshL = await getCourseLessons(id);
                                            setLessons(refreshL?.data || refreshL || []);
                                          } catch (err) {
                                            alert('Failed to delete lesson.');
                                          }
                                        }
                                      }}>
                                        <i className="bi bi-trash" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <Button
                variant="outline-purple"
                size="sm"
                className="w-100 mt-2 btn-download-materials d-flex align-items-center justify-content-center gap-2 fw-bold py-2"
                onClick={() => setShowResourcesModal(true)}
              >
                <i className="bi bi-download" /> Course Materials ({resources.length})
              </Button>
            </div>
          </Col>

          {/* ── RIGHT COLUMN: Main Content ──────────────────────────────── */}
          <Col xl={9} lg={8} className="main-content-col">

            {/* HERO BANNER */}
            <div className="detail-hero-banner mb-3">
              <Row className="g-4 align-items-stretch">

                {/* Hero Left: Image/Video Preview */}
                <Col md={5} xl={5} className="hero-preview-col">
                  <div className="ratio ratio-16x9 h-100 rounded-3 overflow-hidden shadow-sm bg-dark">
                    {activeVideoUrl ? (
                      renderVideoPlayer(activeVideoUrl)
                    ) : (
                      <div className="d-flex align-items-center justify-content-center text-white h-100">
                        <div className="text-center">
                          <i className="bi bi-camera-video-off fs-1 mb-3 d-block opacity-50" />
                          <p className="text-gray-400">No video available</p>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>

                {/* Hero Middle: Title & Instructor */}
                <Col md={7} xl={4} className="hero-details-col">
                  <div>
                    <div className="wrappe mb-2">
                      <Badge
                        bg={priceVal === 0 ? 'success' : 'warning'}
                        className="tag-free-course rounded-pill badge text-bg-success"
                      >
                        {priceVal === 0 ? 'Free Course' : 'Premium Course'}
                      </Badge>
                    </div>
                    <div className="course-title main-heading text-navy mb-2">{courseData.title}</div>

                    {/* Instructor */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <img
                        src={instructorAvatar}
                        alt={instructorName}
                        className="user-avatar"
                        onError={(e) => { e.target.src = DummyUser; }}
                      />
                      <div>
                        <div className="instructor-name text-xs fw-bold text-navy d-flex align-items-center gap-1">
                          {instructorName}
                          {isVerified && <i className="bi bi-patch-check-fill text-primary" />}
                        </div>
                        <div className="instructor-desc text-xs text-gray-700">{instructorLanguages}</div>
                      </div>
                    </div>

                    {/* Ratings */}
                    <div className="d-flex align-items-center gap-3 text-xs text-gray-700 mt-2 flex-wrap">
                      <div className="d-flex align-items-center gap-1">
                        <StarRating rating={parseFloat(ratingVal)} />
                        <strong className="text-dark ms-1">{ratingVal}</strong>
                        <span className="text-secondary">({reviewsCount} {reviewsCount === 1 ? 'Review' : 'Reviews'})</span>
                      </div>
                    </div>

                    <div className="fw-semibold text-sm text-gray-700 d-flex align-items-center gap-1 mt-2">
                      <i className="bi bi-geo-alt-fill text-primary" />
                      <span className="text-primary">Country: <span className="text-navy">{instructorCountry}</span></span>
                    </div>
                  </div>

                  {/* Instructor Mini Stats */}
                  <div className="instructor-mini-stats bg-white border border-light-subtle rounded-3 p-2 d-flex justify-content-between mt-2">
                    <div className="flex-grow-1 text-xs fw-bold border-end border-light-subtle text-center">
                      <div className="text-primary mb-1">Followers</div>
                      <div className="text-navy text-gray-700">{followersCount}</div>
                    </div>
                    <div className="flex-grow-1 text-xs fw-bold border-end border-light-subtle text-center">
                      <div className="text-primary mb-1">Following</div>
                      <div className="text-navy text-gray-700">{followingCount}</div>
                    </div>
                    <div className="flex-grow-1 text-xs fw-bold text-center">
                      <div className="text-primary mb-1">Yrs Exp.</div>
                      <div className="text-navy text-gray-700">{experienceYears}</div>
                    </div>
                  </div>
                </Col>

                {/* Hero Right: Pricing */}
                <Col xl={3} className="hero-pricing-col">
                  <div className="d-flex justify-content-xl-end gap-2 mb-3">
                    <Button variant="light" className="bg-white border flex-center text-xs fw-semibold gap-1 py-2">
                      <i className="bi bi-share" /> Share
                    </Button>
                    <Button variant="light" className="bg-white border flex-center text-xs fw-semibold gap-1 py-2">
                      <i className="bi bi-bookmark" /> Save
                    </Button>
                  </div>

                  <div className="pricing-card p-3 rounded-3 border border-light-subtle">
                    <h2 className="price-tag text-navy mb-2">{priceText}</h2>
                    <div className="enrolled-badge text-xs text-gray-700 mb-2 pb-1 d-flex align-items-center gap-1">
                      <i className="bi bi-people-fill" /> {enrolledCount} Students Enrolled
                    </div>
                    {isEnrolled || isInstructor ? (
                      <div className="d-flex flex-column gap-2">
                        {(progressPct === 100) && (
                          <Button variant="warning" className="w-100 text-white fw-bold text-sm px-3 py-2 mb-1" onClick={handleClaimCertificate}>
                            <i className="bi bi-award-fill me-1" /> Generate Certificate
                          </Button>
                        )}
                        <Button variant="success" className="w-100 fw-bold text-sm text-white px-3 py-2" onClick={() => { setIsPlaying(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                          <i className="bi bi-play-circle me-1" />
                          {isInstructor ? 'Preview Course' : 'Continue Learning'}
                        </Button>
                        {isInstructor && (
                          <div className="d-flex gap-2">
                            <Button variant="outline-primary" className="flex-grow-1 text-xs py-2 fw-bold" onClick={() => setShowEditModal(true)}>
                              <i className="bi bi-pencil-square" /> Edit
                            </Button>
                            <Button variant="outline-danger" className="flex-grow-1 text-xs py-2 fw-bold" onClick={async () => {
                              if (window.confirm('Delete this course?')) {
                                try {
                                  await deleteCourse(id);
                                  alert('Course deleted successfully.');
                                  navigate('/courses');
                                } catch (err) {
                                  alert('Failed to delete course.');
                                }
                              }
                            }}>
                              <i className="bi bi-trash" /> Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : showPaypal ? (
                      <div className="mt-3 w-100">
                        <PayPalScriptProvider options={{ "client-id": "AUp6hwXiJiZ2GZ4W4GlWJfqqO_D3oWaA9ej7FZeoSqeDCUU0wlTsA_VokXzNVBQTtfcYSJovv6qqr0ZR", currency: "USD" }}>
                          <PayPalButtons
                            style={{ layout: "horizontal", height: 40 }}
                            createOrder={(data, actions) => {
                              return actions.order.create({
                                purchase_units: [{
                                  amount: { value: priceVal.toString() }
                                }]
                              });
                            }}
                            onApprove={(data, actions) => {
                              return actions.order.capture().then(async (details) => {
                                try {
                                  setIsEnrolling(true);
                                  const txRes = await storeTransaction({
                                    amount: priceVal,
                                    status: 'succeeded',
                                    currency: 'usd',
                                    charge_id: details.id,
                                    payment_method: 'paypal',
                                    transaction_type: 'course_enrollment'
                                  });

                                  if (txRes && (txRes.status === 200 || txRes.status === 'succeeded' || txRes.data)) {
                                    await registerUserToCourse({
                                      course_id: id,
                                      courseId: id,
                                      payment_method: 'paypal',
                                      transaction_id: details.id
                                    });
                                    setShowPaypal(false);
                                    alert('Enrolled successfully!');
                                    const res = await getCourseDetailById(id);
                                    if (res && res.data) setCourseData(res.data);
                                  } else {
                                    alert('Failed to store transaction. Please contact support.');
                                    setShowPaypal(false);
                                  }
                                } catch (err) {
                                  console.error(err);
                                  alert(err?.response?.data?.message || 'Payment successful but failed to enroll in course on server.');
                                } finally {
                                  setIsEnrolling(false);
                                }
                              });
                            }}
                            onCancel={() => setShowPaypal(false)}
                            onError={(err) => {
                              console.error("PayPal Error:", err);
                              alert("Payment failed or was cancelled.");
                              setShowPaypal(false);
                            }}
                          />
                        </PayPalScriptProvider>
                      </div>
                    ) : (
                      <Button
                        variant="purple"
                        className="w-100 fw-bold text-sm btn-enroll px-3 py-2"
                        disabled={isEnrolling}
                        onClick={async () => {
                          if (priceVal > 0) {
                            setShowPaypal(true);
                          } else {
                            try {
                              setIsEnrolling(true);
                              await registerUserToCourse({ course_id: id, courseId: id });
                              alert('Enrolled successfully!');
                              const res = await getCourseDetailById(id);
                              if (res && res.data) setCourseData(res.data);
                            } catch (err) {
                              alert('Failed to enroll.');
                            } finally {
                              setIsEnrolling(false);
                            }
                          }
                        }}
                      >
                        {isEnrolling ? 'Processing...' : (priceVal > 0 ? `Buy Course – $${priceVal}` : 'Enroll Now')}
                      </Button>
                    )}
                  </div>
                </Col>

              </Row>
            </div>

            {/* FEATURED VIDEO CARD / CURRENT LESSON WORKSPACE */}
            {selectedLesson ? (
              <div className="featured-lesson-card bg-white border border-light-subtle rounded-3 shadow-sm mb-3 p-3">
                <Row className="g-3">
                  <Col md={7}>
                    <div className="fs-5 fw-bold text-navy mb-1">{selectedLesson.title}</div>
                    <p className="text-sm text-gray-700 mb-3">{selectedLesson.description || 'No lesson description provided.'}</p>

                    {lessonLoading ? (
                      <Spinner animation="border" size="sm" variant="primary" />
                    ) : (
                      <div className="d-flex align-items-center gap-2">
                        {(selectedLesson.isVideoCompleted || selectedLesson.is_completed || selectedLesson.completed) ? (
                          <Badge bg="success" className="d-flex align-items-center gap-1 py-2 px-3 rounded-pill text-xs">
                            <i className="bi bi-check-circle-fill text-white" /> Completed
                          </Badge>
                        ) : (
                          <Button variant="success" size="sm" className="fw-bold px-3" onClick={() => handleMarkCompleted(selectedLesson._id)}>
                            Mark completed
                          </Button>
                        )}
                      </div>
                    )}
                  </Col>

                  <Col md={5} className="border-start border-light-subtle">
                    <h6 className="fw-bold text-navy mb-3">Lesson Quiz</h6>
                    {(lessonDetail?.mcqs && lessonDetail.mcqs.length > 0) || (selectedLesson?.mcqs && selectedLesson.mcqs.length > 0) || (selectedLesson?.questions && selectedLesson.questions.length > 0) || (lessonDetail?.questions && lessonDetail.questions.length > 0) ? (
                      <Form onSubmit={(e) => handleSubmitQuizAnswer(e, selectedLesson._id)}>
                        {(lessonDetail?.mcqs || lessonDetail?.questions || selectedLesson?.mcqs || selectedLesson?.questions || []).map((mcq, idx) => {
                          const ans = quizAnswers[mcq._id] || mcq.userAnswer || { mcqId: mcq._id, quizType: mcq.quizType, selectedOptionId: null, selectedOptionIds: [], paragraphAnswer: '' };
                          const isLessonCompleted = selectedLesson?.is_completed || selectedLesson?.completed || lessonDetail?.is_completed || lessonDetail?.completed;
                          const showCorrectAnswer = isInstructor || !!mcq.userAnswer || submittedQuizzes[selectedLesson?._id] || isLessonCompleted;
                          return (
                            <div key={mcq._id} className="mb-4">
                              <p className="fw-semibold text-sm mb-2">{idx + 1}. {mcq.question}</p>
                              {mcq.quizType === 'paragraph' ? (
                                <>
                                  <Form.Control
                                    as="textarea"
                                    rows={2}
                                    className="text-xs"
                                    value={ans.paragraphAnswer || ''}
                                    onChange={(e) => setQuizAnswers({ ...quizAnswers, [mcq._id]: { ...ans, paragraphAnswer: e.target.value } })}
                                    placeholder="Type your answer here..."
                                  />
                                  {(showCorrectAnswer && mcq.paragraphRightAnswer) && (
                                    <div className="text-success text-xs mt-2 fw-medium">
                                      <i className="bi bi-check-circle-fill me-1" /> Correct Answer: {mcq.paragraphRightAnswer}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="d-flex flex-column gap-2">
                                  {mcq.options?.map(opt => {
                                    const isChecked = mcq.quizType === 'multipleChoice'
                                      ? (ans.selectedOptionIds || []).map(String).includes(String(opt._id))
                                      : String(ans.selectedOptionId) === String(opt._id);
                                    return (
                                      <Form.Check
                                        key={opt._id}
                                        type={mcq.quizType === 'multipleChoice' ? 'checkbox' : 'radio'}
                                        id={`opt-${opt._id}`}
                                        name={`quiz-${mcq._id}`}
                                        label={
                                          <span className={`text-sm ${(showCorrectAnswer && opt.isCorrect) ? 'text-success fw-bold' : ''}`}>
                                            {opt.text}
                                            {(showCorrectAnswer && opt.isCorrect) && <i className="bi bi-check-circle-fill ms-2" />}
                                          </span>
                                        }
                                        checked={isChecked}
                                        onChange={(e) => {
                                          let newAns = { ...ans, mcqId: mcq._id, quizType: mcq.quizType };
                                          if (mcq.quizType === 'multipleChoice') {
                                            const ids = new Set((newAns.selectedOptionIds || []).map(String));
                                            if (e.target.checked) ids.add(String(opt._id)); else ids.delete(String(opt._id));
                                            newAns.selectedOptionIds = Array.from(ids);
                                          } else {
                                            newAns.selectedOptionId = String(opt._id);
                                          }
                                          setQuizAnswers({ ...quizAnswers, [mcq._id]: newAns });
                                        }}
                                      />
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <Button type="submit" variant="primary" size="sm" className="w-100 fw-bold">Submit Quiz</Button>
                      </Form>
                    ) : (
                      <div className="text-muted text-sm fst-italic">No quiz available for this lesson.</div>
                    )}
                  </Col>
                </Row>
              </div>
            ) : (
              <div className="featured-lesson-card bg-white border border-light-subtle rounded-3 shadow-sm mb-3 p-3">
                <Row className="g-3 align-items-center">
                  <Col xl={3} sm={4} className="lesson-thumbnail-col">
                    <div className="ratio ratio-16x9 rounded-3 overflow-hidden shadow-sm bg-dark">
                      {coverVideoUrl ? (
                        renderVideoPlayer(coverVideoUrl)
                      ) : (
                        <img src={courseImage} alt="Video Intro" className="w-100 h-100 object-fit-cover" />
                      )}
                    </div>
                  </Col>
                  <Col xl={6} sm={8} className="lesson-text-col">
                    <div className="fs-6 fw-bold text-primary mb-1">Course Introduction</div>
                    <p className="text-sm text-gray-700 mb-2">{courseData.description || 'Watch the intro video to get an overview of this course and what you will learn.'}</p>
                    <div className="d-flex gap-3 text-xs text-gray-700">
                      <span><i className="bi bi-clock me-1" />Intro Video</span>
                      <span><i className="bi bi-play-circle me-1" />Video</span>
                    </div>
                  </Col>
                  <Col xl={3} md={8} className="lesson-action-col text-xl-end">
                    <Button variant="outline-purple" className="btn-custom text-sm" onClick={() => { setIsPlaying(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                      Watch Now
                    </Button>
                  </Col>
                </Row>
              </div>
            )}

            {/* BOTTOM TWO-COLUMN */}
            <Row className="g-3">

              {/* About */}
              <Col xs={12}>
                <div className="bg-white border border-light-subtle rounded-3 shadow-sm p-3">
                  <div className="section-card-title fw-bold text-navy mb-3">About This Course</div>
                  <p className="text-xs text-gray-700 mb-3">
                    {courseData.description || 'No description provided for this course.'}
                  </p>
                  <div className="d-flex flex-wrap gap-3 text-xs text-gray-700">
                    <span><i className="bi bi-tag-fill text-primary me-1" />{categoryName}</span>
                    <span><i className="bi bi-person-fill text-primary me-1" />{enrolledCount} Students</span>
                    <span className="text-capitalize"><i className="bi bi-shield-fill-check text-primary me-1" />{courseType} Course</span>
                  </div>
                </div>
              </Col>

              <Col md={6} xl={4}>
                {/* Your Teacher */}
                <div className="bg-white h-100 border border-light-subtle rounded-3 shadow-sm p-3 text-center">
                  <h5 className="section-card-title fw-bold text-navy text-start mb-3">Your Teacher</h5>

                  <img
                    src={instructorAvatar}
                    alt={instructorName}
                    className="teacher-avatar-circle mx-auto mb-3 rounded-circle object-fit-cover border shadow-sm d-block"
                    style={{ width: '60px', height: '60px' }}
                    onError={(e) => { e.target.src = DummyUser; }}
                  />

                  <h6 className="teacher-name fw-bold text-navy mb-1 d-flex align-items-center justify-content-center gap-1">
                    {instructorName}
                    {isVerified && <i className="bi bi-patch-check-fill text-primary" style={{ fontSize: '0.8rem' }} />}
                  </h6>
                  <p className="teacher-role text-xs text-gray-700 mb-2">{instructorLanguages}</p>

                  <div className="d-flex justify-content-center align-items-center gap-1 text-xs mb-2">
                    <StarRating rating={instructorRating} />
                    <span className="text-secondary ms-1">({instructorReviewCount})</span>
                  </div>

                  <p className="teacher-location text-xs text-gray-700 d-flex align-items-center justify-content-center gap-1 mb-3">
                    <i className="bi bi-geo-alt-fill text-primary" /> {instructorCountry}
                  </p>

                  {/* <div className="d-flex justify-content-center gap-4 text-center text-xs mb-4">
                    <div>
                      <strong className="d-block text-navy">{followersCount}</strong>
                      <span className="text-gray-700 text-xs">Followers</span>
                    </div>
                    <div>
                      <strong className="d-block text-navy">{followingCount}</strong>
                      <span className="text-gray-700 text-xs">Following</span>
                    </div>
                    <div>
                      <strong className="d-block text-navy">{experienceYears}y</strong>
                      <span className="text-gray-700 text-xs">Experience</span>
                    </div>
                  </div> */}

                  <Button variant="outline-purple" className="w-100 btn-custom text-xs fw-bold" onClick={() => navigate(`/teachers/${instructor._id || instructor.id}`)}>
                    View Teacher Profile
                  </Button>
                </div>
              </Col>

              {/* Course Includes */}
              <Col md={6} xl={4}>
                <div className="bg-white h-100 border border-light-subtle rounded-3 shadow-sm p-3">
                  <h5 className="section-card-title fw-bold text-navy mb-3">Course Includes</h5>
                  <ul className="perks-list p-0 m-0 list-unstyled d-flex flex-column gap-2">
                    {[
                      { icon: 'bi bi-camera-video', label: `${totalLessons || 14} Lessons` },
                      { icon: 'bi-play-circle', label: 'On-demand Videos' },
                      { icon: 'bi bi-clipboard-check', label: 'Quizzes' },
                      { icon: 'bi-download', label: 'Downloadable Materials' },
                      { icon: 'bi-award', label: 'Certificate of Completion' },
                      { icon: 'bi-phone', label: 'Access on Mobile and TV' },
                    ].map((perk, idx) => (
                      <li key={idx} className="perk-row d-flex align-items-center gap-3 text-sm text-gray-700">
                        <i className={`bi ${perk.icon} text-primary`} />
                        <span>{perk.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>

              {/* What You'll Get */}
              <Col xl={4}>
                <div className="bg-white h-100 border border-light-subtle rounded-3 shadow-sm p-3">
                  <h5 className="section-card-title fw-bold text-navy mb-3">What You'll Get</h5>
                  <div className="features-icon-list d-flex flex-column gap-2">
                    {[
                      { icon: 'bi-headphones', title: 'Improve Listening', desc: 'Comprehension' },
                      { icon: 'bi-chat-dots', title: 'Real-life Conversation', desc: 'Practice' },
                      { icon: 'bi-journal-richtext', title: 'Build Vocabulary', desc: 'Naturally' },
                      { icon: 'bi-clock', title: 'Learn at Your Own', desc: 'Pace' },
                    ].map((feat, idx) => (
                      <div key={idx} className="feature-item-row d-flex align-items-center gap-3">
                        <div className="feature-icon-wrapper rounded-3 flex-center shadow-xs" style={{ width: '40px', height: '40px' }}>
                          <i className={`bi ${feat.icon}`} />
                        </div>
                        <div className="wrapper">
                          <div className="feature-title text-navy text-sm fw-bold">{feat.title}</div>
                          <div className="feature-desc text-xs text-gray-700">{feat.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>

              <Col xl={8}>
                {/* Reviews */}
                <div className="bg-white border border-light-subtle rounded-3 shadow-sm p-3">
                  <div className="d-flex justify-content-between align-items-center mb-2 pb-1">
                    <h5 className="section-card-title fw-bold text-navy m-0">
                      Course Reviews
                      {reviewsCount > 0 && (
                        <span className="ms-2 badge bg-primary rounded-pill text-xs">{reviewsCount}</span>
                      )}
                    </h5>
                  </div>

                  {courseReviews.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                      {courseReviews.map((rev) => {
                        const reviewer = rev.student || rev.user || {};
                        const reviewerName = safeStr(reviewer.full_name || reviewer.name, 'Anonymous');
                        const reviewerAvatar = reviewer.profile_picture || reviewer.avatar || null;
                        const reviewDate = rev.createdAt ? new Date(rev.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
                        return (
                          <div key={rev._id || rev.id} className="review-item p-3 rounded-3 border border-light-subtle bg-light bg-opacity-25">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2 pb-1">
                              <div className="d-flex align-items-center gap-2">
                                {reviewerAvatar ? (
                                  <img src={reviewerAvatar} alt={reviewerName} className="user-avatar-lg" />
                                ) : (
                                  <div className="user-avatar-lg bg-primary text-white fw-bold text-base">
                                    {reviewerName.charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div>
                                  <div className="fw-bold text-navy text-sm">{reviewerName}</div>
                                  <div className="text-xs text-gray-700">{reviewDate}</div>
                                </div>
                              </div>
                              <div className="ms-auto">
                                <StarRating rating={rev.rating || 0} />
                              </div>
                            </div>
                            <p className="text-sm text-gray-700 mb-0">{rev.review || '—'}</p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="review-empty-state text-center py-4 bg-light bg-opacity-25 rounded-3 border border-light-subtle">
                      <div className="stars-row d-flex align-items-center justify-content-center gap-1 text-gray-700 mb-2">
                        {[1, 2, 3, 4, 5].map((i) => <i key={i} className="bi bi-star" />)}
                      </div>
                      <h6 className="fw-bold text-navy text-xs mb-2">(No Reviews Yet)</h6>
                      <p className="text-xs text-gray-700 mb-3">Be the first to review this course and help others.</p>
                      <Button variant="outline-purple" className="text-xs btn-custom fw-bold" onClick={() => setShowReviewModal(true)}>
                        Write a Review
                      </Button>
                    </div>
                  )}
                </div>
              </Col>

              <Col xl={4}>
                {/* Share */}
                <div className="bg-white border border-light-subtle rounded-3 shadow-sm p-3 text-center">
                  <h5 className="section-card-title fw-bold text-navy text-start mb-3">Share this course</h5>
                  <p className="text-xs text-gray-700 text-start mb-3">Help others discover this course</p>
                  <div className="d-flex justify-content-center gap-3">
                    <Button variant="light" className="rounded-circle share-btn flex-center bg-blue-subtle text-blue" style={{ width: '38px', height: '38px' }}>
                      <i className="bi bi-facebook" />
                    </Button>
                    <Button variant="light" className="rounded-circle share-btn flex-center bg-cyan-subtle text-cyan" style={{ width: '38px', height: '38px' }}>
                      <i className="bi bi-twitter" />
                    </Button>
                    <Button variant="light" className="rounded-circle share-btn flex-center bg-success-subtle text-success" style={{ width: '38px', height: '38px' }}>
                      <i className="bi bi-whatsapp" />
                    </Button>
                    <Button variant="light" className="rounded-circle share-btn flex-center bg-light border border-light-subtle text-secondary" style={{ width: '38px', height: '38px' }}>
                      <i className="bi bi-link-45deg" />
                    </Button>
                  </div>
                </div>
              </Col>

            </Row>
          </Col>

        </Row>
      </Container>

      {/* BOTTOM FLOATING BANNER */}
      <div className="bottom-floating-banner bg-white border-top p-3 shadow-lg mt-5">
        <Container fluid="xl">
          <Row className="g-3 align-items-center">
            <Col xl={6}>
              <div className="banner-left d-flex flex-wrap align-items-center justify-content-center row-gap-2 column-gap-3 pointer rounded-3 p-3" onClick={async () => {
                if (!isEnrolled && !isInstructor) {
                  try {
                    await registerUserToCourse({ course_id: id, courseId: id });
                    alert('Enrolled successfully!');
                    const res = await getCourseDetailById(id);
                    if (res && res.data) setCourseData(res.data);
                    setIsPlaying(true);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } catch (err) {
                    alert('Failed to enroll.');
                  }
                } else {
                  setIsPlaying(true);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}>
                <div className="play-circle-badge bg-white rounded-circle flex-center text-primary" style={{ width: '40px', height: '40px' }}>
                  <i className="bi bi-play-fill fs-4" />
                </div>
                <div className="m-0 text-base fw-medium font-poppins text-white border-end pe-3">
                  {isEnrolled ? 'Continue Learning' : 'Start Course Now'}
                </div>
                <p className="m-0 text-white text-sm">
                  {priceVal > 0 ? `Only ${priceText} – Enroll today!` : "It's free and only takes a few minutes!"}
                </p>
              </div>
            </Col>
            <Col xl={6}>
              <div className="banner-right bg-white row row-cols-1 row-cols-sm-3 g-3">
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="icon bg-purple bg-opacity-10 flex-center rounded-circle" style={{ height: '36px', width: '36px', minWidth: '36px' }}>
                    <i className="bi bi-clock-history fs-6 text-purple" />
                  </span>
                  <div>
                    <div className="text-navy text-sm fw-bold">30-Day Access</div>
                    <div className="text-gray-700 text-xs">Learn at your own pace</div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="icon bg-purple bg-opacity-10 flex-center rounded-circle" style={{ height: '36px', width: '36px', minWidth: '36px' }}>
                    <i className="bi bi-award fs-6 text-purple" />
                  </span>
                  <div>
                    <div className="text-navy text-sm fw-bold">Certificate</div>
                    <div className="text-gray-700 text-xs">Earn when you complete</div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="icon bg-purple bg-opacity-10 flex-center rounded-circle" style={{ height: '36px', width: '36px', minWidth: '36px' }}>
                    <i className="bi bi-headset fs-6 text-purple" />
                  </span>
                  <div>
                    <div className="text-navy text-sm fw-bold">Support</div>
                    <div className="text-gray-700 text-xs">We're here to help</div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* WRITE A REVIEW MODAL */}
      <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-navy">Write a Review</Modal.Title>
        </Modal.Header>
        <Form onSubmit={async (e) => {
          e.preventDefault();
          if (!newReviewText.trim()) return;
          try {
            setSubmittingReview(true);
            await addCourseReview({ course_id: id, rating: newRating, review: newReviewText });
            alert('Review submitted successfully!');
            setShowReviewModal(false);
            setNewReviewText('');
            const reviewsRes = await getCourseReviews(id);
            if (reviewsRes && reviewsRes.data) {
              setCourseReviews(reviewsRes.data);
            } else if (Array.isArray(reviewsRes)) {
              setCourseReviews(reviewsRes);
            }
          } catch (err) {
            console.error('Failed to submit review', err);
            alert('Failed to submit review.');
          } finally {
            setSubmittingReview(false);
          }
        }}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Rating</Form.Label>
              <Form.Select value={newRating} onChange={(e) => setNewRating(Number(e.target.value))}>
                <option value={5}>5 Stars ★★★★★</option>
                <option value={4}>4 Stars ★★★★☆</option>
                <option value={3}>3 Stars ★★★☆☆</option>
                <option value={2}>2 Stars ★★☆☆☆</option>
                <option value={1}>1 Star ★☆☆☆☆</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Your Review</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={newReviewText}
                onChange={(e) => setNewReviewText(e.target.value)}
                placeholder="Share your thoughts about this course..."
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowReviewModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submittingReview}>
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* EDIT COURSE MODAL */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-navy">Edit Course Details</Modal.Title>
        </Modal.Header>
        <Form onSubmit={async (e) => {
          e.preventDefault();
          try {
            const fd = new FormData();
            fd.append('title', editTitle);
            fd.append('description', editDesc);
            fd.append('price', String(editPrice));
            if (editCoverFile) {
              fd.append('cover_image', editCoverFile);
              fd.append('coverImage', editCoverFile);
              fd.append('image', editCoverFile);
            }
            await updateCourse(id, fd);
            alert('Course updated successfully!');
            setShowEditModal(false);
            setEditCoverFile(null);
            const res = await getCourseDetailById(id);
            if (res && res.data) {
              setCourseData(res.data);
            }
          } catch (err) {
            console.error('Failed to update course', err);
            alert('Failed to update course details.');
          }
        }}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Course Title</Form.Label>
              <Form.Control type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Description</Form.Label>
              <Form.Control as="textarea" rows={4} value={editDesc} onChange={(e) => setEditDesc(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Price ($)</Form.Label>
              <Form.Control type="number" min="0" value={editPrice} onChange={(e) => setEditPrice(Number(e.target.value))} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Cover Image</Form.Label>
              <Form.Control type="file" accept="image/*" onChange={(e) => setEditCoverFile(e.target.files[0])} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Save Changes</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* RESOURCES MODAL */}
      <Modal show={showResourcesModal} onHide={() => setShowResourcesModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-navy">Course Materials & Resources</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isInstructor && (
            <Form onSubmit={async (e) => {
              e.preventDefault();
              if (!newResourceName.trim() || !newResourceFile) return;
              try {
                setSubmittingResource(true);
                const fd = new FormData();
                fd.append('course_id', id);
                fd.append('courseId', id);
                fd.append('name', newResourceName);
                fd.append('title', newResourceName);
                fd.append('file', newResourceFile);
                fd.append('pdf', newResourceFile);
                fd.append('pdfs', newResourceFile);
                fd.append('pdf_file', newResourceFile);
                fd.append('pdfFiles', newResourceFile);
                fd.append('pdf_files', newResourceFile);
                await addCourseResources(fd);
                alert('Resource added successfully!');
                setNewResourceName('');
                setNewResourceFile(null);
                const fileInput = document.getElementById('resourceFileInput');
                if (fileInput) fileInput.value = '';

                const resRes = await getCourseResources(id);
                if (resRes && resRes.data) {
                  setResources(resRes.data);
                } else if (Array.isArray(resRes)) {
                  setResources(resRes);
                }
              } catch (err) {
                console.error('Failed to add resource', err);
                alert('Failed to add resource.');
              } finally {
                setSubmittingResource(false);
              }
            }} className="mb-4 p-3 bg-light rounded border">
              <h6 className="fw-bold text-navy mb-3">Add New Resource (Instructor Only)</h6>
              <Row className="g-2">
                <Col md={5}>
                  <Form.Control
                    type="text"
                    placeholder="Resource Name (e.g. Workbook Part 1)"
                    value={newResourceName}
                    onChange={(e) => setNewResourceName(e.target.value)}
                    required
                  />
                </Col>
                <Col md={5}>
                  <Form.Control
                    type="file"
                    id="resourceFileInput"
                    onChange={(e) => setNewResourceFile(e.target.files[0])}
                    required
                  />
                </Col>
                <Col md={2}>
                  <Button type="submit" variant="primary" className="w-100 text-xs py-2 fw-bold" disabled={submittingResource}>
                    {submittingResource ? 'Uploading...' : 'Add'}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}

          {resources.length > 0 ? (
            <div className="d-flex flex-column gap-2">
              {resources.map((resItem) => {
                if (resItem.pdf_files && Array.isArray(resItem.pdf_files) && resItem.pdf_files.length > 0) {
                  return resItem.pdf_files.map((pdf, pdfIdx) => (
                    <div key={pdf._id || `${resItem._id}-${pdfIdx}`} className="d-flex justify-content-between align-items-center p-3 bg-light rounded border border-light-subtle mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-file-earmark-pdf-fill text-danger fs-5" />
                        <span className="fw-semibold text-navy text-sm">{pdf.title || pdf.filename || resItem.title || resItem.name || 'Resource File'}</span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <a
                          href={pdf.fileUrl || pdf.url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary btn-xs py-1 px-3 fw-bold"
                        >
                          <i className="bi bi-eye me-1" /> View
                        </a>
                        {isInstructor && (
                          <Button
                            variant="link"
                            className="text-danger p-0"
                            onClick={async () => {
                              if (window.confirm('Delete this resource?')) {
                                try {
                                  await deleteCourseResources(resItem._id || resItem.id);
                                  alert('Resource deleted.');
                                  const refreshRes = await getCourseResources(id);
                                  if (refreshRes && refreshRes.data) {
                                    setResources(refreshRes.data);
                                  } else if (Array.isArray(refreshRes)) {
                                    setResources(refreshRes);
                                  }
                                } catch (err) {
                                  alert('Failed to delete resource.');
                                }
                              }
                            }}
                          >
                            <i className="bi bi-trash" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ));
                }

                return (
                  <div key={resItem._id || resItem.id} className="d-flex justify-content-between align-items-center p-3 bg-light rounded border border-light-subtle mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-file-earmark-pdf-fill text-danger fs-5" />
                      <span className="fw-semibold text-navy text-sm">{resItem.name || resItem.title || 'Resource File'}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <a
                        href={resItem.url || resItem.fileUrl || resItem.file_url || resItem.pdf || resItem.file || resItem.pdf_file || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary btn-xs py-1 px-3 fw-bold"
                      >
                        <i className="bi bi-eye me-1" /> View
                      </a>
                      {isInstructor && (
                        <Button
                          variant="link"
                          className="text-danger p-0"
                          onClick={async () => {
                            if (window.confirm('Delete this resource?')) {
                              try {
                                await deleteCourseResources(resItem._id || resItem.id);
                                alert('Resource deleted.');
                                const refreshRes = await getCourseResources(id);
                                if (refreshRes && refreshRes.data) {
                                  setResources(refreshRes.data);
                                } else if (Array.isArray(refreshRes)) {
                                  setResources(refreshRes);
                                }
                              } catch (err) {
                                alert('Failed to delete resource.');
                              }
                            }
                          }}
                        >
                          <i className="bi bi-trash" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-muted py-5">
              <i className="bi bi-folder-x fs-1 d-block mb-3 opacity-30" />
              <span>No course materials uploaded yet.</span>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResourcesModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
      {/* CERTIFICATE MODAL */}
      <Modal show={showCertModal} onHide={() => setShowCertModal(false)} centered size="lg">
        <Modal.Body className={`p-4 text-center bg-white border border-4 ${certificateData?.s3Url ? 'border-primary' : 'border-warning'}`}>
          {certificateData?.s3Url ? (
            <div className="d-flex flex-column align-items-center">
              <h2 className="fw-bold mb-4 text-navy">Your Certificate is Ready!</h2>
              <img src={certificateData.s3Url} alt="Certificate" className="img-fluid border rounded shadow-sm mb-4" />
              <div className="d-flex gap-3 justify-content-center">
                <a href={certificateData.s3Url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary fw-bold px-4">
                  <i className="bi bi-box-arrow-up-right me-2" /> Open in New Tab
                </a>
                <a href={certificateData.s3Url} download="Certificate.png" className="btn btn-primary fw-bold px-4">
                  <i className="bi bi-download me-2" /> Download
                </a>
              </div>
            </div>
          ) : (
            <>
              <img src={brandLogo} alt="Yoospeak" style={{ maxHeight: '48px' }} className="mb-3" />
              <h1 className="fw-bold mb-3" style={{ fontFamily: 'Georgia, serif' }}>CERTIFICATE OF COMPLETION</h1>
              <p className="text-muted mb-4">Proudly presented to</p>
              <h2 className="text-primary fw-bold mb-4" style={{ borderBottom: '2px solid #ccc', display: 'inline-block', minWidth: '300px' }}>{certificateData?.studentName}</h2>
              <p className="mb-4">for successfully completing the course:</p>
              <h4 className="fw-bold text-navy mb-5">{certificateData?.courseTitle}</h4>
              <p className="text-xs text-muted">Issued on: {certificateData?.date}</p>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* ADD LESSON MODAL */}
      <Modal show={showAddLessonModal} onHide={() => setShowAddLessonModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-navy">Add Lesson</Modal.Title>
        </Modal.Header>
        <Form onSubmit={async (e) => {
          e.preventDefault();
          try {
            setSubmittingLesson(true);
            const fd = new FormData();
            fd.append('course_id', id);
            fd.append('courseId', id);
            fd.append('title', newLessonTitle);
            fd.append('description', newLessonDesc);

            if (newLessonVideoFile) {
              fd.append('media', newLessonVideoFile);
              fd.append('video', newLessonVideoFile);
              fd.append('video_url', newLessonVideoFile);
              fd.append('videoUrl', newLessonVideoFile);
            } else if (newLessonVideo) {
              fd.append('media', newLessonVideo);
              fd.append('video', newLessonVideo);
              fd.append('video_url', newLessonVideo);
              fd.append('videoUrl', newLessonVideo);
              fd.append('url', newLessonVideo);
              fd.append('youtube_url', newLessonVideo);
              fd.append('youtubeUrl', newLessonVideo);
            }
            if (newLessonQuestions.length > 0) {
              fd.append('questions', JSON.stringify(newLessonQuestions));
            }

            await addCourseLesson(fd);
            alert('Lesson added successfully!');
            setShowAddLessonModal(false);
            setNewLessonTitle('');
            setNewLessonDesc('');
            setNewLessonVideo('');
            setNewLessonVideoFile(null);
            setNewLessonQuestions([]);
            const refreshL = await getCourseLessons(id);
            setLessons(refreshL?.data || refreshL || []);
          } catch (err) {
            console.error('Failed to add lesson', err);
            alert('Failed to add lesson.');
          } finally {
            setSubmittingLesson(false);
          }
        }}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Lesson Title</Form.Label>
              <Form.Control type="text" value={newLessonTitle} onChange={(e) => setNewLessonTitle(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Video URL or File</Form.Label>
              <Form.Control type="text" placeholder="Enter YouTube URL" value={newLessonVideo} onChange={(e) => { setNewLessonVideo(e.target.value); setNewLessonVideoFile(null); }} disabled={!!newLessonVideoFile} className="mb-2" />
              <Form.Control type="file" accept="video/*" onChange={(e) => { setNewLessonVideoFile(e.target.files[0]); setNewLessonVideo(''); }} disabled={!!newLessonVideo} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Description</Form.Label>
              <Form.Control as="textarea" rows={3} value={newLessonDesc} onChange={(e) => setNewLessonDesc(e.target.value)} />
            </Form.Group>
            <QuestionEditor questions={newLessonQuestions} setQuestions={setNewLessonQuestions} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddLessonModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submittingLesson}>
              {submittingLesson ? 'Adding...' : 'Add Lesson'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* EDIT LESSON MODAL */}
      <Modal show={showEditLessonModal} onHide={() => setShowEditLessonModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-navy">Edit Lesson</Modal.Title>
        </Modal.Header>
        <Form onSubmit={async (e) => {
          e.preventDefault();
          try {
            setUpdatingLesson(true);
            const fd = new FormData();
            fd.append('title', editLessonTitle);
            fd.append('description', editLessonDesc);

            if (editLessonVideoFile) {
              fd.append('media', editLessonVideoFile);
              fd.append('video', editLessonVideoFile);
              fd.append('video_url', editLessonVideoFile);
              fd.append('videoUrl', editLessonVideoFile);
            } else if (editLessonVideo) {
              fd.append('media', editLessonVideo);
              fd.append('video', editLessonVideo);
              fd.append('video_url', editLessonVideo);
              fd.append('videoUrl', editLessonVideo);
              fd.append('url', editLessonVideo);
              fd.append('youtube_url', editLessonVideo);
              fd.append('youtubeUrl', editLessonVideo);
            }
            if (editLessonQuestions.length > 0) {
              fd.append('questions', JSON.stringify(editLessonQuestions));
            }
            await updateCourseLesson(editLessonId, fd);
            alert('Lesson updated successfully!');
            setShowEditLessonModal(false);
            const refreshL = await getCourseLessons(id);
            setLessons(refreshL?.data || refreshL || []);
          } catch (err) {
            console.error('Failed to update lesson', err);
            alert('Failed to update lesson.');
          } finally {
            setUpdatingLesson(false);
          }
        }}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Lesson Title</Form.Label>
              <Form.Control type="text" value={editLessonTitle} onChange={(e) => setEditLessonTitle(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Video URL or File</Form.Label>
              <Form.Control type="text" placeholder="Enter YouTube URL" value={editLessonVideo} onChange={(e) => { setEditLessonVideo(e.target.value); setEditLessonVideoFile(null); }} disabled={!!editLessonVideoFile} className="mb-2" />
              <Form.Control type="file" accept="video/*" onChange={(e) => { setEditLessonVideoFile(e.target.files[0]); setEditLessonVideo(''); }} disabled={!!editLessonVideo} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Description</Form.Label>
              <Form.Control as="textarea" rows={3} value={editLessonDesc} onChange={(e) => setEditLessonDesc(e.target.value)} />
            </Form.Group>
            <QuestionEditor questions={editLessonQuestions} setQuestions={setEditLessonQuestions} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditLessonModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={updatingLesson}>
              {updatingLesson ? 'Updating...' : 'Update Lesson'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};
